/**
 * useOrgRoleSurfaces — backend-derived role surfaces for the caller's org
 * (BLU-389 `locations.getOrgRoleSurfaces`), gated on the shared-locations
 * build flag. Mirrors the query usage in LocationManagement.tsx so every
 * consumer shares one cache entry instead of re-issuing the request.
 *
 * BLU-658 / BLU-670: also the source of the two org-shape signals. Both stay
 * false while the query is loading/disabled/erroring — never flash-hide or
 * redirect on an unconfirmed answer.
 *
 * `isPureClient` — SERVER-computed (BLU-670): the org holds an active client
 * junction and owns no location it runs itself. Drives the client IA (client
 * nav sections, /orders inbox, client home, client onboarding steps). The old
 * frontend derivation (`ownedLocationCount === 0`) was unsatisfiable for a
 * properly onboarded client — the claim flow REQUIRES owning the delegated
 * shadow location — and misclassified brand-new zero-location orgs as clients.
 *
 * `hidesProduction` — junction-AGNOSTIC floor signal: the org owns no
 * non-delegated location. Drives Production visibility/guarding and exactly
 * mirrors the backend `requireOwnedLocation` middleware (trpc.ts), so the
 * nav can never show a workspace the API 403s (a brand-new zero-location org
 * is NOT a pure client but still has no Production floor).
 */
import { trpc } from '@/lib/trpc';
import type { OrgRoleSurfaces } from '@threadheads/shared';

export interface UseOrgRoleSurfacesResult {
  surfaces: OrgRoleSurfaces | undefined;
  isLoading: boolean;
  /** Server-computed pure-client verdict — client IA switch. */
  isPureClient: boolean;
  /** True once resolved AND the org runs no floor of its own — hide Production. */
  hidesProduction: boolean;
}

/**
 * Mount behaviour for a read that only needs the answer already held (a
 * per-row hint, say). Omitted, every observer refetches a stale answer on
 * mount, which is what the guards and the sidebar rely on.
 */
export interface UseOrgRoleSurfacesOptions {
  refetchOnMount?: boolean;
  retryOnMount?: boolean;
}

export function useOrgRoleSurfaces(options: UseOrgRoleSurfacesOptions = {}): UseOrgRoleSurfacesResult {
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  const query = trpc.locations.getOrgRoleSurfaces.useQuery(undefined, {
    enabled: sharedLocationsEnabled,
    staleTime: 60_000,
    ...options,
  });

  return {
    surfaces: query.data,
    isLoading: query.isLoading,
    isPureClient: query.isSuccess && query.data.isPureClient === true,
    hidesProduction:
      query.isSuccess && query.data.ownedNonDelegatedLocationCount === 0,
  };
}
